import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SEOHead from '@/components/feature/SEOHead';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Quelle est la différence entre une pompe à chaleur air-eau et air-air ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "La pompe à chaleur air-eau chauffe l'eau de votre circuit de chauffage (radiateurs, plancher chauffant) et peut produire l'eau chaude sanitaire. Elle est idéale pour remplacer une chaudière. La pompe à chaleur air-air diffuse directement l'air chaud dans les pièces via des splits muraux. Elle est réversible et peut climatiser en été."
      }
    },
    {
      "@type": "Question",
      "name": "Proposez-vous un service de dépannage d'urgence ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, HB SERVICES propose un service de dépannage d'urgence 24h/7j à Genève et environs pour : panne de chauffage en hiver, fuite d'eau importante, panne électrique totale, et problèmes de chauffe-eau. Intervention en moins d'1 heure pour les urgences critiques."
      }
    },
    {
      "@type": "Question",
      "name": "Quand faut-il mettre aux normes son installation électrique ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "La mise aux normes électrique NIBT est obligatoire à Genève lors d'une rénovation importante, d'un changement de locataire, ou si l'installation a plus de 30 ans. Les points critiques : absence de disjoncteur différentiel 30mA, tableau électrique vétuste, câblage en plomb, prises sans terre."
      }
    },
    {
      "@type": "Question",
      "name": "Comment détecter une fuite d'eau cachée ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Les signes d'une fuite cachée : augmentation inexpliquée de la facture d'eau, traces d'humidité ou moisissures, compteur qui tourne robinets fermés, bruit d'eau dans les murs. HB SERVICES utilise des technologies de détection non destructive : caméra thermique, gaz traceur, et électro-acoustique."
      }
    },
    {
      "@type": "Question",
      "name": "Pourquoi installer une VMC double flux ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "La VMC double flux renouvelle l'air intérieur tout en récupérant 90% de la chaleur de l'air extrait. Avantages : qualité d'air optimale, économies de chauffage (30% de réduction), confort (pas de courants d'air froid), et silence. Obligatoire dans les constructions Minergie à Genève."
      }
    },
    {
      "@type": "Question",
      "name": "Proposez-vous des contrats d'entretien ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, HB SERVICES propose des contrats d'entretien annuels pour tous vos équipements à Genève : chaudière gaz/mazout/pellets, pompe à chaleur, VMC, adoucisseur d'eau, et installation électrique. Coût : 250 à 500 CHF/an selon les équipements."
      }
    }
  ]
};

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqCategories = [
    {
      category: "Pompes à Chaleur",
      icon: "ri-temp-hot-line",
      questions: [
        {
          q: "Quelle est la différence entre une pompe à chaleur air-eau et air-air ?",
          a: "La pompe à chaleur air-eau chauffe l'eau de votre circuit de chauffage (radiateurs, plancher chauffant) et peut produire l'eau chaude sanitaire. Elle est idéale pour remplacer une chaudière. La pompe à chaleur air-air diffuse directement l'air chaud dans les pièces via des splits muraux. Elle est réversible et peut climatiser en été. À Genève, nous recommandons l'air-eau pour les rénovations et l'air-air pour les constructions neuves ou appartements."
        },
        {
          q: "Combien coûte l'installation d'une pompe à chaleur à Genève ?",
          a: "Le prix d'une installation de pompe à chaleur à Genève varie entre 25'000 et 45'000 CHF selon la puissance (8 à 16 kW), le type (air-eau ou air-air), et la complexité de l'installation. Ce prix inclut la fourniture, la pose, le raccordement hydraulique et électrique, et la mise en service. Des subventions cantonales et communales peuvent réduire le coût de 30 à 40%. Nous vous aidons dans toutes les démarches administratives."
        },
        {
          q: "Quelles sont les subventions disponibles pour une pompe à chaleur à Genève ?",
          a: "Le Canton de Genève offre des subventions importantes pour le remplacement d'un chauffage fossile par une pompe à chaleur : jusqu'à 15'000 CHF pour une PAC air-eau et 20'000 CHF pour une PAC géothermique. Les communes (Carouge, Meyrin, Vernier, Lancy, etc.) proposent des aides complémentaires. Le programme fédéral peut ajouter 3'000 à 5'000 CHF. HB SERVICES vous accompagne gratuitement dans toutes vos demandes de subventions."
        },
        {
          q: "Une pompe à chaleur fonctionne-t-elle en hiver à Genève ?",
          a: "Oui, les pompes à chaleur modernes fonctionnent parfaitement en hiver à Genève, même par températures négatives jusqu'à -20°C. Les PAC air-eau récentes utilisent du fluide frigorigène R32 ou R290 qui reste efficace par grand froid. Le COP (coefficient de performance) reste supérieur à 2.5 même à -10°C, ce qui signifie 2.5 kWh de chaleur produits pour 1 kWh électrique consommé. Pour les hivers rigoureux, un appoint électrique intégré prend le relais si nécessaire."
        },
        {
          q: "Quel entretien pour une pompe à chaleur ?",
          a: "L'entretien annuel d'une pompe à chaleur est obligatoire à Genève pour les installations de plus de 10 kW. Il comprend : vérification du circuit frigorifique, contrôle des pressions, nettoyage des filtres et échangeurs, test des sécurités, contrôle électrique et mesure des performances. Coût : 250 à 350 CHF/an. Un entretien régulier garantit un COP optimal, prolonge la durée de vie (20-25 ans) et maintient la garantie constructeur. HB SERVICES propose des contrats d'entretien annuels."
        }
      ]
    },
    {
      category: "Installation Électrique",
      icon: "ri-flashlight-line",
      questions: [
        {
          q: "Quand faut-il mettre aux normes son installation électrique ?",
          a: "La mise aux normes électrique NIBT (Norme d'Installation à Basse Tension) est obligatoire à Genève lors d'une rénovation importante, d'un changement de locataire, ou si l'installation a plus de 30 ans. Les points critiques : absence de disjoncteur différentiel 30mA, tableau électrique vétuste, câblage en plomb, prises sans terre, surcharge du tableau. Une installation non conforme présente des risques d'électrocution et d'incendie. HB SERVICES réalise un diagnostic gratuit et un devis détaillé."
        },
        {
          q: "Combien coûte une rénovation électrique complète à Genève ?",
          a: "Le coût d'une rénovation électrique à Genève varie selon la surface : 8'000 à 12'000 CHF pour un 3 pièces (70m²), 15'000 à 25'000 CHF pour un 5 pièces (120m²), et 30'000 à 50'000 CHF pour une villa (200m²). Ce prix inclut : nouveau tableau électrique avec différentiels 30mA, recâblage complet, prises et interrupteurs, éclairage LED, mise à la terre, et contrôle OIBT. Options : domotique KNX, prises USB, détecteurs de mouvement, variateurs."
        },
        {
          q: "Qu'est-ce qu'un disjoncteur différentiel et pourquoi est-il obligatoire ?",
          a: "Le disjoncteur différentiel 30mA protège les personnes contre l'électrocution en détectant les fuites de courant. Il coupe l'alimentation en 30 millisecondes si un courant de fuite dépasse 30mA (par exemple si vous touchez un fil dénudé). Obligatoire depuis 1990 en Suisse, il doit protéger tous les circuits : prises, éclairage, cuisine, salle de bain. À Genève, l'OIBT (Office cantonal de l'Inspection et des relations du travail) contrôle sa présence lors des vérifications d'installations."
        },
        {
          q: "Peut-on installer une borne de recharge pour voiture électrique chez soi ?",
          a: "Oui, l'installation d'une wallbox (borne de recharge) est possible à Genève, en maison ou en PPE (copropriété). Puissances disponibles : 3.7 kW (16A mono), 7.4 kW (32A mono), 11 kW (16A tri) ou 22 kW (32A tri). L'installation nécessite : câble renforcé depuis le tableau, disjoncteur magnéto-thermique adapté, différentiel type A ou B, et déclaration OIBT. Coût : 1'500 à 3'500 CHF selon la distance et la puissance. Subventions cantonales disponibles jusqu'à 500 CHF."
        },
        {
          q: "Qu'est-ce que la domotique KNX et quels sont ses avantages ?",
          a: "KNX est le standard européen de domotique filaire pour maison connectée. Il permet de contrôler éclairage, chauffage, volets roulants, alarme et vidéosurveillance depuis un smartphone ou une tablette. Avantages : économies d'énergie (30% sur chauffage et éclairage), confort (scénarios automatiques), sécurité (simulation de présence), et évolutivité (ajout de fonctions sans travaux). À Genève, KNX est recommandé pour les villas et appartements haut de gamme. Coût : 150 à 300 CHF par point de commande."
        }
      ]
    },
    {
      category: "Plomberie & Sanitaire",
      icon: "ri-drop-line",
      questions: [
        {
          q: "Comment détecter une fuite d'eau cachée ?",
          a: "Les signes d'une fuite cachée : augmentation inexpliquée de la facture d'eau, traces d'humidité ou moisissures, compteur qui tourne robinets fermés, bruit d'eau dans les murs. HB SERVICES utilise des technologies de détection non destructive : caméra thermique (détecte les variations de température), gaz traceur (localise précisément la fuite), et électro-acoustique (écoute les bruits d'eau). Intervention rapide à Genève et environs. Coût détection : 250 à 500 CHF selon la complexité."
        },
        {
          q: "Combien coûte la rénovation d'une salle de bain à Genève ?",
          a: "Le prix d'une rénovation de salle de bain à Genève varie selon la surface et les équipements : 15'000 à 25'000 CHF pour 5m², 25'000 à 40'000 CHF pour 8m², et 40'000 à 60'000 CHF pour 12m². Ce prix inclut : démolition, évacuation, plomberie complète, électricité, carrelage, douche italienne ou baignoire, meuble vasque, WC suspendu, robinetterie, et finitions. Options haut de gamme : Geberit, Hansgrohe, Villeroy & Boch, Duravit. Délai : 2 à 4 semaines."
        },
        {
          q: "Faut-il installer un adoucisseur d'eau à Genève ?",
          a: "L'eau de Genève est très calcaire (dureté 30-40°fH selon les quartiers). Le calcaire endommage chaudière, chauffe-eau, robinetterie et électroménager, et augmente la consommation d'énergie de 20 à 30%. Un adoucisseur d'eau protège vos installations, améliore le confort (peau, cheveux), et réduit l'utilisation de produits d'entretien. Coût : 2'500 à 4'500 CHF installé. Entretien : ajout de sel régénérant (15 CHF/mois). Durée de vie : 15-20 ans. Investissement rentabilisé en 5-7 ans."
        },
        {
          q: "Que faire en cas de fuite d'eau urgente ?",
          a: "En cas de fuite d'eau importante : 1) Coupez l'arrivée d'eau générale (vanne principale), 2) Coupez l'électricité si l'eau touche des prises ou appareils, 3) Contactez HB SERVICES 24h/7j au [numéro urgence], 4) Prenez des photos pour l'assurance, 5) Éponger l'eau pour limiter les dégâts. Notre plombier d'urgence intervient en moins d'1 heure à Genève et environs. Détection de fuite, réparation immédiate, et rapport pour assurance. Tarif urgence : majoration 50% nuit/weekend."
        },
        {
          q: "Quelle est la différence entre un chauffe-eau électrique et thermodynamique ?",
          a: "Le chauffe-eau électrique (boiler) chauffe l'eau par résistance électrique. Consommation : 3'000 à 4'000 kWh/an pour 4 personnes. Le chauffe-eau thermodynamique utilise une pompe à chaleur intégrée qui capte les calories de l'air ambiant. Consommation : 800 à 1'200 kWh/an, soit 70% d'économies ! Coût : 3'500 à 5'500 CHF installé (vs 1'500 à 2'500 CHF pour un électrique). Subventions cantonales : 1'000 à 1'500 CHF. Rentabilité : 4-6 ans. Durée de vie : 15-20 ans."
        }
      ]
    },
    {
      category: "Ventilation",
      icon: "ri-windy-line",
      questions: [
        {
          q: "Pourquoi installer une VMC double flux ?",
          a: "La VMC (Ventilation Mécanique Contrôlée) double flux renouvelle l'air intérieur tout en récupérant 90% de la chaleur de l'air extrait. Avantages : qualité d'air optimale (filtration pollens, pollution), économies de chauffage (30% de réduction), confort (pas de courants d'air froid), et silence. Obligatoire dans les constructions Minergie à Genève. Idéale pour les maisons passives et rénovations BBC. Coût : 12'000 à 25'000 CHF selon la surface. Subventions disponibles pour les rénovations énergétiques."
        },
        {
          q: "Quelle différence entre VMC simple flux et double flux ?",
          a: "La VMC simple flux extrait l'air vicié des pièces humides (cuisine, salle de bain, WC) et crée une dépression qui fait entrer l'air neuf par des grilles dans les pièces de vie. Coût : 3'000 à 6'000 CHF. La VMC double flux utilise un échangeur thermique qui récupère 90% de la chaleur de l'air extrait pour préchauffer l'air neuf entrant. Coût : 12'000 à 25'000 CHF. À Genève, le double flux est recommandé pour les maisons bien isolées et les rénovations énergétiques."
        },
        {
          q: "Quel entretien pour une VMC ?",
          a: "L'entretien d'une VMC est essentiel pour maintenir la qualité d'air et les performances. VMC simple flux : nettoyage des bouches d'extraction tous les 3 mois, remplacement des filtres annuel, contrôle du moteur tous les 3 ans. VMC double flux : remplacement des filtres tous les 6 mois (pollens, particules fines), nettoyage de l'échangeur thermique annuel, désinfection des gaines tous les 5 ans. Coût entretien annuel : 150 à 300 CHF. HB SERVICES propose des contrats d'entretien."
        },
        {
          q: "La VMC est-elle bruyante ?",
          a: "Les VMC modernes sont très silencieuses : 25 à 35 dB(A) en vitesse normale, soit moins qu'un réfrigérateur. Le bruit provient du moteur et du flux d'air dans les gaines. Pour un silence optimal : caisson VMC dans un local technique isolé, gaines acoustiques, bouches d'extraction silencieuses, et vitesse adaptée. La VMC double flux est plus silencieuse que la simple flux car l'air neuf est insufflé (pas de dépression). À Genève, nous installons des VMC haut de gamme (Zehnder, Vallox, Paul) garanties silencieuses."
        },
        {
          q: "Peut-on installer une VMC en rénovation ?",
          a: "Oui, l'installation d'une VMC en rénovation est possible à Genève, même sans faux plafond. Solutions : gaines apparentes dans les combles ou sous-sol, gaines plates dans les cloisons, VMC décentralisée par pièce (sans gaines), ou VMC simple flux hygroréglable (moins de gaines). La VMC double flux nécessite plus de travaux (réseau de gaines aller et retour) mais offre de meilleures performances. Coût rénovation : 6'000 à 15'000 CHF selon la configuration. Subventions cantonales disponibles."
        }
      ]
    },
    {
      category: "Maintenance & Dépannage",
      icon: "ri-tools-line",
      questions: [
        {
          q: "Quand faut-il entretenir sa chaudière ?",
          a: "L'entretien annuel de la chaudière est obligatoire à Genève pour les chaudières gaz, mazout et bois. Période recommandée : septembre-octobre avant l'hiver. L'entretien comprend : ramonage du corps de chauffe, nettoyage du brûleur, contrôle de combustion (CO, CO2, rendement), réglage des paramètres, test des sécurités, et contrôle d'étanchéité. Coût : 200 à 350 CHF selon le type. Un entretien régulier réduit la consommation de 10%, évite les pannes, et prolonge la durée de vie de 5 à 10 ans."
        },
        {
          q: "Proposez-vous un service de dépannage d'urgence ?",
          a: "Oui, HB SERVICES propose un service de dépannage d'urgence 24h/7j à Genève et environs pour : panne de chauffage en hiver, fuite d'eau importante, panne électrique totale, et problèmes de chauffe-eau. Intervention en moins d'1 heure pour les urgences critiques. Nos techniciens sont équipés de pièces détachées courantes pour réparer immédiatement. Tarif urgence : tarif normal + majoration 50% (nuit, weekend, jours fériés). Contactez-nous au [numéro urgence] pour toute urgence."
        },
        {
          q: "Que faire si ma chaudière ne démarre plus ?",
          a: "Vérifications avant d'appeler un technicien : 1) Contrôlez que le disjoncteur n'a pas sauté, 2) Vérifiez la pression d'eau (doit être entre 1 et 1.5 bar), 3) Regardez si un code erreur s'affiche, 4) Testez le thermostat d'ambiance, 5) Vérifiez que le robinet de gaz est ouvert. Si la chaudière ne démarre toujours pas, contactez HB SERVICES. Causes fréquentes : pression d'eau trop basse, électrode d'allumage défectueuse, circulateur bloqué, ou sonde de température HS. Dépannage : 150 à 400 CHF."
        },
        {
          q: "Combien de temps dure une chaudière ?",
          a: "La durée de vie d'une chaudière dépend du type et de l'entretien : chaudière gaz standard 15-20 ans, chaudière gaz à condensation 20-25 ans, chaudière mazout 20-25 ans, chaudière à pellets 15-20 ans. Un entretien annuel rigoureux peut prolonger la durée de vie de 5 à 10 ans. Signes de remplacement : pannes fréquentes, consommation excessive, bruit anormal, rendement en baisse, ou pièces détachées introuvables. À Genève, nous recommandons le remplacement par une pompe à chaleur (subventions importantes)."
        },
        {
          q: "Proposez-vous des contrats d'entretien ?",
          a: "Oui, HB SERVICES propose des contrats d'entretien annuels pour tous vos équipements à Genève : chaudière gaz/mazout/pellets, pompe à chaleur, VMC, adoucisseur d'eau, et installation électrique. Avantages : tarif préférentiel, priorité en cas de panne, dépannage inclus ou à tarif réduit, prolongation de garantie, et tranquillité d'esprit. Coût : 250 à 500 CHF/an selon les équipements. Paiement mensuel possible. Contactez-nous pour un devis personnalisé."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="FAQ Chauffage Électricité Plomberie Genève | Questions Fréquentes - HB SERVICES"
        description="Réponses à toutes vos questions sur le chauffage, l'électricité, la plomberie et la ventilation à Genève. Pompes à chaleur, NIBT, VMC, dépannage urgence 24/7 en Suisse romande."
        keywords="FAQ CVSE Genève, questions chauffage Genève, pompe chaleur questions, électricité NIBT questions, plomberie Genève FAQ, ventilation VMC questions"
        canonical={`${import.meta.env.VITE_SITE_URL}/faq`}
        schema={faqSchema}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 to-teal-800 py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Questions Fréquentes (FAQ)
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Toutes les réponses à vos questions sur le chauffage, l'électricité, la plomberie et la ventilation à Genève
          </p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className={`${category.icon} text-2xl text-teal-600`}></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
              </div>
              
              <div className="space-y-4">
                {category.questions.map((faq, index) => {
                  const globalIndex = categoryIndex * 100 + index;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <div 
                      key={index}
                      className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-600 transition-all"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.q}
                        </h3>
                        <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-teal-600 flex-shrink-0`}></i>
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-5">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.a}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Vous ne trouvez pas la réponse à votre question ?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Notre équipe d'experts est à votre disposition pour répondre à toutes vos questions
          </p>
          <a 
            href="#contact"
            className="inline-block px-8 py-4 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-lg"
          >
            Contactez-nous
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
